import React, { useEffect, useState } from "react";
import { useLocation, useSearchParams, useNavigate } from "react-router-dom";
import { getStatus } from "../services/flightBookingAPI";
import { auth } from "../firebase";
import { toast } from "react-toastify";
import { useAuth } from "../context/AuthContext";
import ProcessingModal from "../components/modal/ProcessingModal";
import FailureModal from "../components/modal/FailureModal";
import SuccessModal from "../components/modal/SuccessModal";
import BookingFailedModal from "../components/modal/BookingFailedModal";
import BookingConfirmation from "../components/bookingFlight/BookingConfirmation";
import BookingReviewError from "../components/bookingFlight/BookingReviewError";
import "../../src/components/componetsChat/style/skeleton.css";

const FligthBooking = () => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);

  // Modal Stages: 'validating', 'booking', 'success', 'payment_failed', 'booking_failed', 'none'
  const [modalStage, setModalStage] = useState('validating');
  const { openLoginModal } = useAuth();
  const [showBookingDetails, setShowBookingDetails] = useState(false);
  const [ticketData, setTicketData] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [currentBookingStatus, setCurrentBookingStatus] = useState("");

  // Track if minimum display time for "Validating" has passed
  const [minTimePassed, setMinTimePassed] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setMinTimePassed(true), 2000);
    return () => clearTimeout(timer);
  }, []);

  // Handle transition from validating -> booking

  useEffect(() => {
    if (modalStage === 'validating' && minTimePassed) {
      // Transition to booking stage when payment is successful OR ticket is already issued
      if (currentBookingStatus === 'PAYMENT_SUCCESS' || currentBookingStatus === 'TICKET_ISSUED' || currentBookingStatus === 'SUCCESS' || currentBookingStatus === 'BOOKING_IN_PROGRESS') {
        setModalStage('booking');
      }
    }
  }, [modalStage, minTimePassed, currentBookingStatus]);

  // Auto-close booking modal after 1 second if ticket is already issued
  useEffect(() => {
    if (modalStage === 'booking' && ticketData) {
      const timer = setTimeout(() => {
        setModalStage('none');
        setShowBookingDetails(true);
        setIsLoading(false);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [modalStage, ticketData]);

  // Prevent back navigation/refresh during critical steps
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (modalStage === 'validating' || modalStage === 'booking') {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    const handlePopState = (e) => {
      if (modalStage === 'validating' || modalStage === 'booking') {
        window.history.pushState(null, '', window.location.href);
        alert("Please do not go back while your booking is being processed.");
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('popstate', handlePopState);

    // Push an initial state to detect the first back button press
    window.history.pushState(null, '', window.location.href);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('popstate', handlePopState);
    };
  }, [modalStage]);

  useEffect(() => {
    let pollingTimer;
    let attempts = 0;
    const MAX_ATTEMPTS = 60; // Approx 3 minutes with 3s interval

    const checkStatus = async () => {
      const rc_booking_id_param = searchParams.get("rc_booking_id");
      const trace_id_param = searchParams.get("trace_id");
      const result_idx_param = searchParams.get("result_idx");

      const stateData = location.state || {};
      const storedBookingId = localStorage.getItem("rc_booking_id");

      let rc_booking_id = storedBookingId || rc_booking_id_param || stateData.rc_booking_id;
      let trace_id = stateData.trace_id || trace_id_param;
      let result_idx = stateData.result_idx || result_idx_param;
      let amount = stateData.amount;

      // Recover Auth State
      const user = auth.currentUser;
      let userToken = stateData.userToken;
      const contactDetails = stateData.contactDetails;
      let userContact = user?.email || user?.phoneNumber || "";

      // If no login email/phone, fallback to lead contact details from state
      if (!userContact && contactDetails) {
        userContact = contactDetails.email || contactDetails.phone || "";
      }

      // If user is present, ensure we have the token
      if (user) {
        userToken = userToken || await user.getIdToken();
      } else {
        // Check local storage fallbacks only if not logged in
        const storedToken = localStorage.getItem("userToken");
        const storedEmail = localStorage.getItem("userEmail") || localStorage.getItem("email");
        const storedUser = localStorage.getItem("user");

        if (storedToken && !userToken) userToken = storedToken;
        if (storedEmail && !userContact) userContact = storedEmail;

        if (storedUser && (!userToken || !userContact)) {
          try {
            const parsedUser = JSON.parse(storedUser);
            userToken = userToken || parsedUser.token || parsedUser.accessToken;
            userContact = userContact || parsedUser.email || parsedUser.phone || parsedUser.phoneNumber || "";
          } catch (e) {
            console.warn("[FlightBooking] Failed to parse stored user:", e.message);
          }
        }
      }

      if (!rc_booking_id) {
        setModalStage('none');
        navigate("/book-flight");
        return;
      }

      try {
        attempts++;

        const response = await getStatus(
          rc_booking_id, trace_id, result_idx, amount, userToken, userContact,
          contactDetails?.phone, contactDetails?.email
        );

        const bookingData = response?.data?.bookings?.[0] || response?.bookings?.[0] || response?.data?.[0];
        const status = bookingData?.status;
        const message = response?.message;

        setCurrentBookingStatus(status);

        // 1. Success Case (Ticket Issued)
        if (status === "TICKET_ISSUED" || status === "SUCCESS") {
          toast.success(message || "Ticket booked successfully!", { position: "top-center" });

          let processedData = { ...bookingData };
          if (typeof processedData.ticket_details === 'string') {
            try { processedData.ticket_details = JSON.parse(processedData.ticket_details); } catch (e) { }
          }
          if (typeof processedData.booking_request === 'string') {
            try { processedData.booking_request = JSON.parse(processedData.booking_request); } catch (e) { }
          }

          setTicketData(processedData);

          // Auto-close is handled by useEffect that watches modalStage and ticketData
          // This ensures the booking modal is visible for at least 1 second after appearing
          return;
        }

        // 2. Payment Failure
        if (status === "PAYMENT_FAILED" || status === "FAILED") {
          toast.error(message || "Payment verification failed.", { position: "top-center" });
          setErrorMessage(message || "Payment verification failed.");
          setModalStage('payment_failed');
          setIsLoading(false);
          return;
        }

        // 3. Booking Failure (Payment was success, but ticket failed)
        if (status === "TICKET_FAILED" || status === "ORDER_FAILED") {
          toast.error(message || "Booking failed.", { position: "top-center" });
          setTicketData(bookingData);
          setModalStage('booking_failed');
          setIsLoading(false);
          return;
        }

        // Continue Polling
        if (attempts < MAX_ATTEMPTS) {
          pollingTimer = setTimeout(checkStatus, 3000);
        } else {
          setModalStage('none');
          setIsLoading(false);
        }

      } catch (error) {
        console.warn("[FlightBooking] Polling error:", error);

        // Handle terminal errors (e.g., Booking not found)
        const isBookingNotFound =
          error.message?.includes("Booking not found") ||
          error.response?.status === 404 ||
          String(error).includes("404") ||
          (error.originalError && (
            error.originalError.response?.status === 404 ||
            error.originalError.message?.includes("Booking not found") ||
            error.originalError.response?.data?.message?.includes("Booking not found")
          ));

        if (isBookingNotFound) {
          console.error("[FlightBooking] Terminal error: Booking not found");
          setErrorMessage("We couldn't retrieve your booking details. This usually happens if the session expired or the booking ID is no longer valid. If you were already charged, please check your email for confirmation or contact support.");
          setModalStage('payment_failed');
          setIsLoading(false);
          return;
        }

        // Check for 401 or Auth errors
        const isAuthError =
          error.status === 401 ||
          error.response?.status === 401 ||
          error.message?.toLowerCase().includes("auth verifier") ||
          error.message?.toLowerCase().includes("authentication required");

        if (isAuthError) {
          setModalStage('error');
          setErrorMessage("Authentication required to verify your booking status. Please sign in.");
          setIsLoading(false);
          openLoginModal();
          return;
        }

        if (attempts < MAX_ATTEMPTS) {
          pollingTimer = setTimeout(checkStatus, 3000);
        } else {
          setErrorMessage("Verification is taking longer than expected. Please check 'My Bookings' in a few minutes to see your status.");
          setModalStage('payment_failed');
          setIsLoading(false);
        }
      }
    };

    const checkStatusWrapper = async () => {
      // Small delay to allow auth to initialize if it's the first load
      // Increase wait to 3.5 seconds for slower cold starts on reload
      if (!auth.currentUser) {
        await new Promise(resolve => {
          const timeout = setTimeout(() => {
            console.warn("[FlightBooking] Auth wait timed out");
            resolve(null);
          }, 3500);

          const unsubscribe = auth.onAuthStateChanged(user => {
            clearTimeout(timeout);
            unsubscribe();
            resolve(user);
          });
        });
      }
      checkStatus();
    };

    checkStatusWrapper();
    return () => {
      clearTimeout(pollingTimer);
    };
  }, [searchParams, location.state, navigate]);
  return (
    <>
      {/* Modals */}
      {modalStage === 'validating' && (
        <ProcessingModal
          title="Validating Payment"
          message="We are currently confirming your transaction with the bank."
        />
      )}

      {modalStage === 'booking' && (
        <ProcessingModal
          title="Booking Your Flight"
          message="Payment confirmed! We are now issuing your ticket with the airline."
        />
      )}

      {modalStage === 'payment_failed' && (
        <FailureModal
          title={errorMessage?.includes("couldn't retrieve") ? "Booking Not Found" : "Payment Failed"}
          errorMessage={errorMessage}
          retryLabel={
            errorMessage?.includes("couldn't retrieve")
              ? (location.state?.source === "search" ? "Back to Search" : "Back to Home")
              : "Try Again"
          }
          onRetry={() => {
            if (errorMessage?.includes("couldn't retrieve")) {
              if (location.state?.source === "search") {
                navigate(`/search-form${location.state?.searchQuery || ""}`);
              } else {
                navigate('/');
              }
            } else {
              window.location.reload();
            }
          }}
          onContactSupport={() => navigate('/chat')}
        />
      )}

      {modalStage === 'booking_failed' && (
        <BookingFailedModal
          bookingId={ticketData?.rc_booking_id}
          ticketData={ticketData}
          source={location.state?.source || "chat"}
          searchQuery={location.state?.searchQuery || ""}
          onClose={() => setModalStage('none')}
        />
      )}
      
      {modalStage === 'error' && (
        <BookingReviewError 
          errorMessage={errorMessage}
          onRetry={() => window.location.reload()}
          onSignIn={() => openLoginModal()}
          source={location.state?.source}
          searchQuery={location.state?.searchQuery}
        />
      )}

      <div className={`mt-16 min-h-screen bg-[#F6F8FA] transition-all duration-500 ${modalStage !== 'none' ? 'blur-md pointer-events-none' : ''}`}>
        {/* Main */}
        <main className="mx-auto max-w-[1200px] px-4 md:px-6 py-4 md:py-6 space-y-4 md:space-y-6">
          {showBookingDetails && ticketData ? (
            <BookingConfirmation
              onContactSupport={() => navigate('/chat')}
              flightDetails={
                ticketData?.ticket_details?.FlightItinerary ||
                ticketData?.Response?.Response?.FlightItinerary ||
                ticketData?.FlightItinerary ||
                ticketData?.booking_request?.FlightItinerary ||
                ticketData?.booking_request?.Response?.Response?.FlightItinerary || ticketData?.booking_request?.Response?.FlightItinerary ||
                null
              }
              bookingData={ticketData}
            />
          ) : (
            <>
              {/* Skeleton Hero card */}
              <section className="bg-white rounded-[12px] shadow-[0_1px_2px_rgba(0,0,0,0.06)] p-4 md:p-6 space-y-4">
                <div className="mx-auto h-28 w-[90%] rounded-[12px] shimmer" />
                <div className="bg-[#F7F9FB] rounded-[12px] h-28 md:h-32 shimmer" />
              </section>

              {/* Skeleton Two-column grid */}
              <section className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-6">
                <div className="md:col-span-7 space-y-3">
                  <div className="bg-white rounded-[12px] shadow-sm p-4 space-y-3 h-64 shimmer" />
                </div>
                <div className="md:col-span-5 space-y-3">
                  <div className="bg-white rounded-[12px] shadow-sm p-4 space-y-3 h-64 shimmer" />
                </div>
              </section>
            </>
          )}
        </main>
      </div>
    </>
  );
};

export default FligthBooking;
